/**
 * Iter122 — Case → Time tab
 *
 * Simplified per directive: no billable/non-billable split. Investigator can
 * Start Operation / End; sees Start Time, End Time, today's total, and
 * personal history for THIS case. Each user's time entries are private
 * (visible only to that user and admin). Admins can approve, edit, or deny
 * entries through the top-level Field Operations admin module.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from '../../hooks/use-toast';
import { BACKEND_URL } from '../../config/canonical';
import { Play, Square, RefreshCw, Clock, CheckCircle2, Loader2 } from 'lucide-react';

const ACTIVITIES = [
  'SURVEILLANCE', 'INTERVIEW', 'RESEARCH', 'SKIP_TRACE', 'VEHICLE_SEARCH',
  'PERSON_SEARCH', 'COURT_RESEARCH', 'REPORT_WRITING', 'CLIENT_COMMUNICATION',
  'TRAVEL', 'ADMINISTRATIVE', 'OTHER',
];

const fmtHMS = (secs) => {
  const s = Math.max(0, Math.floor(secs || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
};
const dt = (v) => v ? new Date(v).toLocaleString() : '—';

export default function TimeBillingTab({ caseId }) {
  const token = localStorage.getItem('nexus_token');
  const H = { Authorization: `Bearer ${token}` };
  const JH = { ...H, 'Content-Type': 'application/json' };

  const [active, setActive] = useState(null);
  const [entries, setEntries] = useState([]);
  const [todayTotal, setTodayTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [activity, setActivity] = useState('SURVEILLANCE');
  const [notes, setNotes] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ar, cr, tr] = await Promise.all([
        fetch(`${BACKEND_URL}/api/time-entries/me/active`, { headers: H }),
        fetch(`${BACKEND_URL}/api/cases/${caseId}/time-entries`, { headers: H }),
        fetch(`${BACKEND_URL}/api/time-entries/me/today`, { headers: H }),
      ]);
      const av = ar.ok ? await ar.json() : null;
      const cd = cr.ok ? await cr.json() : { entries: [] };
      const td = tr.ok ? await tr.json() : { total_seconds_today: 0 };
      setActive(av && av.case_id === caseId ? av : null);
      setEntries(cd.entries || []);
      setTodayTotal(td.total_seconds_today || 0);
    } finally { setLoading(false); }
    // eslint-disable-next-line
  }, [caseId, token]);

  useEffect(() => { load(); }, [load]);

  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active || active.status !== 'ACTIVE') return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active]);
  const liveSeconds = active
    ? Math.max(0, Math.floor((now - new Date(active.start_time).getTime()) / 1000) - (active.paused_seconds || 0))
    : 0;

  const start = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/time-entries/start`, {
        method: 'POST', headers: JH,
        body: JSON.stringify({ case_id: caseId, activity, notes }),
      });
      const d = await r.json();
      if (!r.ok) return toast({ title: 'Could not start', description: d.detail || 'Error', variant: 'destructive' });
      setNotes('');
      toast({ title: 'Operation started', description: `Now tracking ${activity.replace(/_/g, ' ').toLowerCase()}.` });
      await load();
    } finally { setBusy(false); }
  };

  const end = async () => {
    if (!active) return;
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/time-entries/${active.id}/end`, { method: 'POST', headers: JH, body: JSON.stringify({}) });
      const d = await r.json();
      if (!r.ok) toast({ title: 'Failed', description: d.detail || 'Error', variant: 'destructive' });
      else toast({ title: 'Ended' });
      await load();
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="case-time-billing-tab">
      <div className="rounded-lg border border-slate-700 bg-[#131A2A] p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <Clock className="w-5 h-5 text-emerald-400" />
            <div>
              <div className="text-xs text-slate-400 uppercase tracking-wide">Current Operation</div>
              {active ? (
                <div className="text-lg font-semibold text-white" data-testid="active-time-timer">
                  {fmtHMS(liveSeconds)}
                  <span className="ml-2 text-xs text-slate-400">· {active.activity?.replace(/_/g, ' ')}</span>
                  <Badge className={`ml-2 text-[10px] ${active.status === 'ACTIVE' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300'}`}>
                    {active.status}
                  </Badge>
                </div>
              ) : (
                <div className="text-sm text-slate-500">No active operation on this case.</div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!active && (
              <>
                <Select value={activity} onValueChange={setActivity}>
                  <SelectTrigger className="bg-slate-800 border-slate-600 text-white w-44" data-testid="time-activity-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ACTIVITIES.map(a => <SelectItem key={a} value={a}>{a.replace(/_/g, ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button className="bg-emerald-600 hover:bg-emerald-500 text-white" disabled={busy} onClick={start} data-testid="time-start-btn">
                  {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />} Start Operation
                </Button>
              </>
            )}
            {active && (
              <Button className="bg-red-600 hover:bg-red-500 text-white" disabled={busy} onClick={end} data-testid="time-end-btn">
                <Square className="w-4 h-4 mr-1" /> End
              </Button>
            )}
          </div>
        </div>
        {!active && (
          <Textarea
            value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Optional notes for this operation…"
            className="mt-3 bg-slate-800 border-slate-600 text-white text-sm" rows={2}
            data-testid="time-notes-input"
          />
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="rounded-lg border border-emerald-700/40 bg-emerald-950/30 p-3">
          <div className="text-xs text-emerald-300 uppercase">Total Time Today</div>
          <div className="text-xl font-semibold text-emerald-200">{fmtHMS(todayTotal)}</div>
        </div>
        <div className="rounded-lg border border-slate-700 bg-[#131A2A] p-3">
          <div className="text-xs text-slate-400 uppercase">Entries on This Case</div>
          <div className="text-xl font-semibold text-white">{entries.length}</div>
        </div>
      </div>

      <div className="rounded-lg border border-slate-700 bg-[#131A2A] overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2 border-b border-slate-700">
          <div className="text-sm font-semibold text-white">My Time Entry History (this case)</div>
          <Button variant="ghost" size="sm" onClick={load} className="text-slate-300 hover:text-white">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        {entries.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-500">No time entries yet.</div>
        ) : (
          <div className="divide-y divide-slate-800">
            {entries.map(e => (
              <div key={e.id} className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`time-entry-${e.id}`}>
                <div className="min-w-0">
                  <div className="text-sm text-white font-medium">
                    {e.activity?.replace(/_/g, ' ')}
                    <Badge className="ml-2 text-[10px] bg-slate-700 text-slate-200">{e.status}</Badge>
                    {e.approval_status === 'APPROVED' && (
                      <Badge className="ml-1 text-[10px] bg-blue-500/20 text-blue-300"><CheckCircle2 className="w-3 h-3 mr-0.5 inline" /> Approved</Badge>
                    )}
                    {e.approval_status === 'DENIED' && (
                      <Badge className="ml-1 text-[10px] bg-red-500/20 text-red-300">Denied</Badge>
                    )}
                  </div>
                  <div className="text-xs text-slate-400 flex items-center gap-2 flex-wrap">
                    <span>Start: {dt(e.start_time)}</span>
                    <span>· End: {dt(e.end_time)}</span>
                    <span>· {fmtHMS(e.duration_seconds)}</span>
                  </div>
                  {e.notes && <div className="text-xs text-slate-300 mt-1 whitespace-pre-wrap">{e.notes}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
